import React, { Component } from 'react';
import { Row, Col } from 'antd';
import { connect } from 'react-redux';
import { dispatch, IRootDispatch, IRootState, state } from 'stores/rematch/root-store';
import { ScheduleType } from 'utilities/enum-utils';
import { IGroupServiceSchedule } from 'interfaces/service-interfaces';
import moment from 'moment-timezone';
import _ from 'lodash';
import { Title, Paragraph, Text } from 'common-components/typography';
import { PrimaryButton, SecondaryButton } from 'common-components/buttons';
import { useGroupScheduleProgress } from 'stores/hooks/query-hooks/use-group-schedule-progress';
import { notifications } from '@good/ui/notifications';

interface ISummaryStepPanelProps {
  newGroupServiceSchedule: IGroupServiceSchedule;
  selectedGroupService: typeof state.groupServiceStore.selectedGroupService;
  doCreateNewSession: typeof dispatch.servicesStore.doCreateNewSession;
  setNewGroupServiceSchedule: typeof dispatch.servicesStore.setNewGroupServiceSchedule;
  doFetchGroupServiceOverview: typeof dispatch.groupServiceStore.doFetchGroupServiceOverview;
  doFetchGroupServiceDetail: typeof dispatch.servicesStore.doFetchGroupServiceDetail;
  onNextStep: () => void;
  onPreviousStep?: () => void;
  closeAddSessionModal: () => void;
  resetAndCloseModal: () => void;
}

interface ISummaryStepPanelState {
  isLoading: boolean;
}

// Wrapper component to use the hook
const SummaryStepPanelWithHook: React.FC<ISummaryStepPanelProps> = (props) => {
  const { startProgressTracking } = useGroupScheduleProgress({
    onSuccess: () => {
      // Job completed successfully - just show a notification
      notifications.show({
        title: 'Schedule created successfully',
        message: 'Your schedule has been created and is ready to use.',
        color: 'green',
        autoClose: 5000,
        withCloseButton: false,
      });
    },
    onError: (error) => {
      notifications.show({
        title: 'Schedule creation failed',
        message: error,
        color: 'red',
        autoClose: 3000,
        withCloseButton: false,
      });
    },
  });

  return <SummaryStepPanel {...props} startProgressTracking={startProgressTracking} />;
};

interface ISummaryStepPanelWithHookProps extends ISummaryStepPanelProps {
  startProgressTracking: (jobId: string, message: string) => void;
}

class SummaryStepPanel extends Component<ISummaryStepPanelWithHookProps, ISummaryStepPanelState> {
  state = {
    isLoading: false,
  };

  private _createSession = async () => {
    const {
      newGroupServiceSchedule,
      doCreateNewSession,
      selectedGroupService,
      startProgressTracking,
      resetAndCloseModal,
    } = this.props;

    try {
      this.setState({ isLoading: true });
      const sessionToSend =
        newGroupServiceSchedule.scheduleType === ScheduleType.ONE_OFF
          ? newGroupServiceSchedule.sessions
          : _.filter(newGroupServiceSchedule.sessions, (session) => session.isSelected);
      const result = await doCreateNewSession({
        ...newGroupServiceSchedule,
        address: newGroupServiceSchedule.address
          ? {
              ...newGroupServiceSchedule.address,
              geoLat: Number(newGroupServiceSchedule.address.geoLat),
              geoLng: Number(newGroupServiceSchedule.address.geoLng),
            }
          : null,
        serviceId: selectedGroupService.serviceId,
        serviceDepartmentId: selectedGroupService.serviceDepartmentId,
        sessions: _.map(sessionToSend, (session) => {
          return {
            ...session,
            activityGroups: _.map(session.activityGroups, (activityGroup) => ({
              ...activityGroup,
              startDateTime: moment(activityGroup.startDateTime).tz(selectedGroupService.timezone, true).format(),
              endDateTime: moment(activityGroup.endDateTime).tz(selectedGroupService.timezone, true).format(),
            })),
            startDateTime: moment(session.startDateTime).tz(selectedGroupService.timezone, true).format(),
            endDateTime: moment(session.endDateTime).tz(selectedGroupService.timezone, true).format(),
          };
        }),
        scheduleStartDate: moment(newGroupServiceSchedule.scheduleStartDate)
          .tz(selectedGroupService.timezone, true)
          .format(),
        scheduleEndDate: moment(newGroupServiceSchedule.scheduleEndDate)
          .tz(selectedGroupService.timezone, true)
          .format(),
        timeSlots: _.map(newGroupServiceSchedule.timeSlots, (timeSlot) => {
          return {
            ...timeSlot,
            activityGroups: _.map(timeSlot.activityGroups, (activityGroup) => ({
              ...activityGroup,
              startDateTime: moment(activityGroup.startDateTime).tz(selectedGroupService.timezone, true).format(),
              endDateTime: moment(activityGroup.endDateTime).tz(selectedGroupService.timezone, true).format(),
            })),
            startDateTime: moment(timeSlot.startDateTime).tz(selectedGroupService.timezone, true).format(),
            endDateTime: moment(timeSlot.endDateTime).tz(selectedGroupService.timezone, true).format(),
          };
        }),
      });

      // Check if this is a queued operation (202 response)
      if (result?.jobId) {
        // For queued operations, start progress tracking and reset/close wizard immediately
        startProgressTracking(result.jobId, 'Creating your group schedule... This may take a few moments.');
        resetAndCloseModal();
      } else if (result?.serviceDateTimeId) {
        // Immediate success with serviceDateTimeId
        await this.props.setNewGroupServiceSchedule({
          ...newGroupServiceSchedule,
          serviceDateTimeId: result.serviceDateTimeId,
        });
        resetAndCloseModal();
      } else {
        // Generic success - reset/close immediately
        resetAndCloseModal();
      }
    } catch (error) {
      console.error('Error creating session:', error);
      notifications.show({
        title: 'Schedule creation failed',
        message: error.message || 'An error occurred while creating the schedule',
        color: 'red',
        autoClose: 3000,
        withCloseButton: false,
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  render() {
    const { newGroupServiceSchedule, onPreviousStep } = this.props;
    const { isLoading } = this.state;

    return (
      <div className='anim-slide-left'>
        <Row className='ph-x4-large'>
          <Col span={6} style={{ position: 'sticky', top: '0px', height: 'calc(100vh - 88px)', overflow: 'auto' }}>
            <div className='width-3/4'>
              <Title level={4}>Review</Title>
              <Paragraph>Review the details before you create the group schedule.</Paragraph>
            </div>
          </Col>
          <Col span={18} className='pl-large' style={{ minHeight: 'calc(100vh - 88px)' }}>
            <div className='mv-large'>
              <Title level={3}>Schedule Summary</Title>
              <Paragraph>Please review your schedule details before creating:</Paragraph>

              <div className='mv-medium'>
                <Text weight='bold'>Schedule Name:</Text> {newGroupServiceSchedule.scheduleName}
              </div>

              <div className='mv-medium'>
                <Text weight='bold'>Schedule Type:</Text> {newGroupServiceSchedule.scheduleType}
              </div>

              <div className='mv-medium'>
                <Text weight='bold'>Start Date:</Text>{' '}
                {moment(newGroupServiceSchedule.scheduleStartDate).format('MMMM Do, YYYY')}
              </div>

              <div className='mv-medium'>
                <Text weight='bold'>End Date:</Text>{' '}
                {moment(newGroupServiceSchedule.scheduleEndDate).format('MMMM Do, YYYY')}
              </div>

              <div className='mv-medium'>
                <Text weight='bold'>Number of Sessions:</Text> {newGroupServiceSchedule.sessions?.length || 0}
              </div>
            </div>

            <div
              className='pv-medium width-full'
              style={{ backgroundColor: 'rgba(248,249,250,0.7)', position: 'sticky', bottom: 0 }}
            >
              <Row gutter={0} type='flex' align='middle' justify='space-between' className='bg-transparent'>
                <div className='text-align-right pv-medium'>
                  <SecondaryButton size='large' disabled={isLoading} onClick={onPreviousStep}>
                    Back
                  </SecondaryButton>
                </div>
                <div className='text-align-right pv-medium'>
                  <PrimaryButton size='large' loading={isLoading} onClick={this._createSession}>
                    Create Schedule
                  </PrimaryButton>
                </div>
              </Row>
            </div>
          </Col>
        </Row>
      </div>
    );
  }
}

const mapState = (state: IRootState) => ({
  newGroupServiceSchedule: state.servicesStore.newGroupServiceSchedule,
  selectedGroupService: state.groupServiceStore.selectedGroupService,
});

const mapDispatch = (dispatch: IRootDispatch) => ({
  doCreateNewSession: dispatch.servicesStore.doCreateNewSession,
  setNewGroupServiceSchedule: dispatch.servicesStore.setNewGroupServiceSchedule,
  doFetchGroupServiceOverview: dispatch.groupServiceStore.doFetchGroupServiceOverview,
  doFetchGroupServiceDetail: dispatch.servicesStore.doFetchGroupServiceDetail,
});

export default connect(mapState, mapDispatch)(SummaryStepPanelWithHook);
